import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useProfile } from '@/hooks/useProfile';
import {
  type DateFormat,
  type TimeFormat,
  type DateStyle,
  formatUserDate,
  formatUserTime,
  formatUserDateTime,
} from '@/lib/format-date';

interface DateFormatContextType {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  formatDate: (date: Date | string, style?: DateStyle) => string;
  formatTime: (date: Date | string) => string;
  formatDateTime: (date: Date | string, dateStyle?: DateStyle) => string;
}

const defaults: DateFormatContextType = {
  dateFormat: 'mdy',
  timeFormat: '12h',
  formatDate: (date, style = 'short') =>
    formatUserDate(date, { dateFormat: 'mdy', timeFormat: '12h' }, style),
  formatTime: (date) =>
    formatUserTime(date, { dateFormat: 'mdy', timeFormat: '12h' }),
  formatDateTime: (date, dateStyle = 'short') =>
    formatUserDateTime(date, { dateFormat: 'mdy', timeFormat: '12h' }, dateStyle),
};

const DateFormatContext = createContext<DateFormatContextType>(defaults);

export function DateFormatProvider({ children }: { children: React.ReactNode }) {
  const { profile } = useProfile();

  const dateFormat: DateFormat = (profile?.date_format as DateFormat) || 'mdy';
  const timeFormat: TimeFormat = (profile?.time_format as TimeFormat) || '12h';

  const prefs = useMemo(() => ({ dateFormat, timeFormat }), [dateFormat, timeFormat]);

  const formatDate = useCallback(
    (date: Date | string, style: DateStyle = 'short') => formatUserDate(date, prefs, style),
    [prefs],
  );

  const formatTime = useCallback(
    (date: Date | string) => formatUserTime(date, prefs),
    [prefs],
  );

  const formatDateTime = useCallback(
    (date: Date | string, dateStyle: DateStyle = 'short') => formatUserDateTime(date, prefs, dateStyle),
    [prefs],
  );

  const value = useMemo(
    () => ({ dateFormat, timeFormat, formatDate, formatTime, formatDateTime }),
    [dateFormat, timeFormat, formatDate, formatTime, formatDateTime],
  );

  return (
    <DateFormatContext.Provider value={value}>
      {children}
    </DateFormatContext.Provider>
  );
}

export function useDateFormat() {
  return useContext(DateFormatContext);
}
